import store from "@/store";
import { OrderConfirmation } from "@/models/order-details/interfaces/order";
import { CartItem, CartItems } from "@/models/shopping-cart-item/interfaces/cart";
import { Modifiers } from "@/models/modifiers/interfaces/modifiers";
import {
  AddItemSources,
  ScanAndGoRejectReasons,
  TrackEvent,
  TrackEventField,
  GenericEvent,
  TrackEventActions,
  TrackEventObjects,
  RewardType,
  LinkingMethod
} from "@/models/utils/interfaces/pages-track";
import { APPLICATION_NAME_FRIENDLY, MAPI_API_TYPE, SENTRY_ERRORS } from "@/../app-constants";
import { PaymentOrderDetail } from "@/models/payment/interfaces/payment";
import { createGtm } from "@gtm-support/vue-gtm";
import { SEND_ANALYTICS_EVENTS } from "@appetize/iw-common/utils/featureFlags";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";

const trackEvent = (eventObject: TrackEvent | GenericEvent) => {
  window.dataLayer?.push({ ecommerce: null });
  window.dataLayer?.push(eventObject);
};

const getVariants = (modifiers: Modifiers): string => {
  const keys = Object.keys(modifiers);

  if (keys.length === 0) return "";

  return keys.reduce((acc, key) => {
    const modifier = modifiers[key][0];
    const { id, name, cost } = modifier || "";
    acc = `${acc} id:${id}, name:${name}, cost:${cost},`;
    return acc;
  }, "");
};

const getProductsDetail = (cartItems: CartItems[]) => {
  return cartItems.map((cartItem) => {
    const { item, modifiers, quantity } = cartItem;
    const mods = getVariants(modifiers);
    return {
      id: item.id,
      name: item.name,
      price: `${item.cost}`,
      brand: "",
      category: item.type,
      variant: mods,
      quantity: +quantity
    };
  });
};

const getPaymentDetail = (paymentDetail: PaymentOrderDetail[]) => {
  return paymentDetail.map((p) => {
    return {
      paymentType: p.selectedTender?.paymentType || p.paymentType,
      subpaymentType: p.selectedTender?.subpaymentType || p.subpaymentType,
      selection: p.selectedTender?.selection,
      selectionType: p.selectedTender?.selectionType,
      amount: p.amount,
      tax: p.tax
    };
  });
};

export const pushLoyaltyDetailEvent = (
  orderId: string,
  paymentType: string,
  subpaymentType: string,
  providerName: string,
  rewardType: RewardType,
  rewardAmount: number,
  linkingMethod: LinkingMethod
): void => {
  const trackObject: GenericEvent = {
    event: TrackEventField.GENERIC_EVENT,
    eventInfo: {
      location: "interactWeb",
      action: TrackEventActions.PURCHASE,
      object: TrackEventObjects.LOYALTY_PROVIDER_USED
    },
    eventMetadata: {
      orderId,
      paymentType,
      subpaymentType,
      providerName,
      rewardType,
      rewardAmount,
      linkingMethod
    }
  };

  trackEvent(trackObject);
};

export const pushLoyaltyDetailEvents = (orderId: string): void => {
  if (store.getters["venueSettings/APIType"] === MAPI_API_TYPE) return;

  const reportedAmenities = store.getters["cart/getProcessedAmenities"]?.reportedAmenities || [];

  //loop thru amenities to push stored value events
  reportedAmenities.forEach((amenity) => {
    amenity.payments.forEach((payment) => {
      const tenderName =
        store.getters["vendor/getTenders"]?.find(
          (tender) => tender.paymentType === amenity.paymentType && tender.subpaymentType === amenity.subpaymentType
        )?.name || "";
      pushLoyaltyDetailEvent(
        orderId,
        amenity.paymentType,
        amenity.subpaymentType,
        tenderName,
        "storedValue",
        Number(payment.spent),
        amenity.autoApplied ? "autoApplied" : "manuallyApplied"
      );
    });
  });

  const cartDiscounts = store.getters["cart/getOAPICart"]?.appliedDiscounts || [];
  const flatReductions = store.getters["cart/getFlatReductions"];

  //Loop thru all discounts returned from calculate cart
  cartDiscounts.forEach((discount) => {
    //Check every discount tags to see if any matches a reduction
    discount.tags.forEach((tag) => {
      //look for reductions matching tag
      const reduction = flatReductions.find((reduction) => `${reduction.reductionId}` === tag);
      if (reduction) {
        pushLoyaltyDetailEvent(
          orderId,
          reduction.selectedTender.paymentType,
          reduction.selectedTender.subpaymentType,
          reduction.selectedTender.name,
          "reduction",
          Number(discount.reduction_computation.value * -1),
          reduction.selectedTender.autoApplied ? "autoApplied" : "manuallyApplied"
        );
      }
    });

    //discounts with no reference_id are asumed to be PPI discounts applied by the cart endpoint
    if (!discount.reference_id) {
      pushLoyaltyDetailEvent(orderId, "", "", discount.name, "discount", Number(discount.reduction_computation.value * -1), "");
    }
  });
};

export const trackPurchase = (order: OrderConfirmation, cartItems: CartItems[]) => {
  if (!order || !cartItems || !window.dataLayer) return;

  const { target, _payments } = order;

  const actionField = {
    id: `${target.orderId}`,
    affiliation: APPLICATION_NAME_FRIENDLY,
    revenue: `${_payments?.reduce((a, b) => {
      return a + (+b?.amount || 0);
    }, 0)}`,
    tax: `${_payments?.reduce((a, b) => {
      return a + (+b?.tax || 0);
    }, 0)}`,
    shipping: "0",
    coupon: ""
  };
  const trackObject: TrackEvent = {
    event: TrackEventField.PURCHASE,
    userId: store.getters["user/getGenericUserId"],
    vendorId: `${store.getters["vendor/getActiveVendorId"] || ""}`,
    ecommerce: {
      purchase: {
        actionField,
        products: getProductsDetail(cartItems),
        payments: getPaymentDetail(_payments)
      }
    },
    eventMetaData: {
      spotOnApi: store.getters["venueSettings/APIType"],
      userLoginStatus: store.getters["user/getUserLoginStatus"]
    }
  };

  trackEvent(trackObject);
  pushLoyaltyDetailEvents(target.orderId);
};

export const trackCartAction = (
  eventName: TrackEventField,
  item: CartItem,
  modifiers: Modifiers,
  quantity: number,
  source: AddItemSources = AddItemSources.MENU
) => {
  if (!window.dataLayer) return;

  const ecommerceObjectKey = getEcommerceObjectKey(eventName);
  const trackObject = {
    event: eventName,
    userId: store.getters["user/getGenericUserId"],
    vendorId: `${store.getters["vendor/getActiveVendorId"] || ""}`,
    ecommerce: {
      currencyCode: "USD",
      [ecommerceObjectKey]: {
        products: [
          {
            name: item.name,
            id: item.id,
            price: `${item.cost}`,
            brand: "",
            category: item.type,
            variant: getVariants(modifiers),
            quantity: quantity
          }
        ]
      }
    },
    eventMetaData: {
      spotOnApi: store.getters["venueSettings/APIType"],
      source
    }
  };

  trackEvent(trackObject);
};

export const trackFailedScanAndGoAdd = (barcode: string, source: AddItemSources, reason: ScanAndGoRejectReasons) => {
  if (!window.dataLayer) return;

  const trackObject: GenericEvent = {
    event: TrackEventField.GENERIC_EVENT,
    eventInfo: {
      location: "interactWeb",
      action: TrackEventActions.BARCODE_PROCESSED,
      object: TrackEventObjects.FAILED_TO_ADD_BARCODE
    },
    eventMetadata: {
      spotOnApi: store.getters["venueSettings/APIType"],
      barcode,
      source,
      reason
    }
  };

  trackEvent(trackObject);
};

export const trackCheckoutView = (cartItems: CartItems[]) => {
  if (!window.dataLayer) return;

  const trackObject: TrackEvent = {
    event: TrackEventField.CHECKOUT,
    userId: store.getters["user/getGenericUserId"],
    vendorId: `${store.getters["vendor/getActiveVendorId"] || ""}`,
    ecommerce: {
      checkout: {
        actionField: { step: 1 },
        products: getProductsDetail(cartItems)
      }
    },
    eventMetaData: {
      spotOnApi: store.getters["venueSettings/APIType"]
    },
    eventCallback: () => {}
  };

  trackEvent(trackObject);
};

const getEcommerceObjectKey = (eventName: TrackEventField): string => {
  switch (eventName) {
    case TrackEventField.ADD:
      return "add";
    case TrackEventField.REMOVE:
      return "remove";
    default:
      return "";
  }
};

export const trackProductClicks = (product: CartItem) => {
  if (!window.dataLayer) return;

  const trackObject: TrackEvent = {
    event: TrackEventField.CLICK,
    userId: store.getters["user/getGenericUserId"],
    vendorId: `${store.getters["vendor/getActiveVendorId"] || ""}`,
    ecommerce: {
      click: {
        actionField: { list: "Menu" },
        products: [
          {
            name: product.name,
            id: product.id,
            price: `${product.cost}`,
            brand: "",
            category: product.type,
            variant: "",
            position: ""
          }
        ]
      }
    },
    eventMetaData: {
      spotOnApi: store.getters["venueSettings/APIType"]
    },
    eventCallback: () => {}
  };

  trackEvent(trackObject);
};

export const addGTMPlugin = async (app, router) => {
  try {
    //fetch feature flag to check if IW is loading GTM
    const featureFlag = await store.dispatch("featureFlags/fetchFeatureFlagsById", SEND_ANALYTICS_EVENTS, { root: true });
    app.use(
      createGtm({
        id: process.env.VUE_APP_GOOGLE_TAG_MANAGER_CODE || "",
        defer: false,
        enabled: featureFlag[SEND_ANALYTICS_EVENTS],
        debug: false, //used to be true only for development mode
        loadScript: true,
        vueRouter: router,
        trackOnNextTick: false,
        vueRouterAdditionalEventData: () => ({
          userId: store.getters["user/getGenericUserId"],
          vendorId: `${store.getters["vendor/getActiveVendorId"] || ""}`
        })
      })
    );
  } catch (error: any) {
    errorLoggerAdapter.createIssue(
      SENTRY_ERRORS.ERROR_ADDING_GTM_PLUGIN,
      "pagesTrack",
      "addGTMPlugin",
      { description: error.toString() || "" },
      error
    );
  }
};
